import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Box, Typography, useTheme } from '@mui/material';
import CustomIconButton from './CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Reset.svg';
import arrowUp from 'packages/novous_web/src/assets/icons/ArrowBlack.svg';
import arrowDown from 'packages/novous_web/src/assets/icons/ArrowDownBlack.svg';
import dragAndDrop from 'packages/novous_web/src/assets/icons/Drag and Move.svg';
import CustomDropDownInput from './customDropDownInput';
import DropDownIcon from 'packages/novous_web/src/assets/icons/ArrowDownIcon.svg';

type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};

type TilesObjType = {
  [key: string]: {
    name: string;
    status: boolean;
    selected?: string;
  };
};

interface CustomiseTableModalProps {
  open: boolean;
  onClose: () => void;
  tilesObj?: TilesObjType;
  onReset?: () => void;
  title?: string;
  description?: string;
  columnObj: ColumnObjType;
  setTilesObj?: React.Dispatch<React.SetStateAction<TilesObjType>>;
  setColumnObj: React.Dispatch<React.SetStateAction<ColumnObjType>>;
  children?: React.ReactNode;
}
interface OptionType {
  value: string;
  label: string;
  disabled?: boolean;
}

const CustomiseTableModal: React.FC<CustomiseTableModalProps> = ({
  open,
  onClose,
  onReset,
  title = 'Customise Table',
  description = 'Choose which sections to display and arrange them the way you like.',
  columnObj,
  setColumnObj,
  setTilesObj,
  tilesObj,
}) => {
  const [tempColumnObj, setTempColumnObj] = useState<ColumnObjType>(columnObj);
  const [overviewExpanded, setOverviewExpanded] = useState(true);
  const [dataTableExpanded, setDataTableExpanded] = useState(true);

  // State for managing dropdown selections via React hooks.
  const [tileSelections, setTileSelections] = useState<Record<string, string>>({});

  // Store all possible stat names from the initial tilesObj (on first mount)
  // This ensures options remain available even when stats are deselected
  const [allPossibleStats, setAllPossibleStats] = useState<Set<string>>(new Set());

  // Capture all stat names on first mount
  useEffect(() => {
    if (tilesObj && allPossibleStats.size === 0) {
      const initialStats = new Set<string>();
      Object.keys(tilesObj).forEach((key) => {
        const statName = tilesObj[key].name || tilesObj[key].selected;
        if (statName && statName !== 'none' && statName !== '') {
          initialStats.add(statName);
        }
      });
      setAllPossibleStats(initialStats);
    }
  }, [tilesObj, allPossibleStats.size]);

  // Initialize tileSelections from tilesObj when available.
  // FIX: Initialize tileSelections when modal opens or tilesObj changes
  // FIX: Initialize tileSelections from current selections when modal opens
  useEffect(() => {
    if (tilesObj && open) {
      const currentSelections = Object.keys(tilesObj).reduce((acc, key) => {
        // If the tile is disabled (status: false), it means "none" was selected
        // Otherwise, use the tile's name
        acc[key] = tilesObj[key].status ? tilesObj[key].name : 'none';
        return acc;
      }, {} as Record<string, string>);
      setTileSelections(currentSelections);
    }
  }, [tilesObj, open]);

  useEffect(() => {
    if (open) {
      setTempColumnObj(columnObj);
    }
  }, [open, columnObj]);

  const toggleOverview = () => {
    setOverviewExpanded((prev) => !prev);
  };

  const toggleDataTable = () => {
    setDataTableExpanded((prev) => !prev);
  };

  const handleCheckboxChange = (key: keyof ColumnObjType) => {
    setTempColumnObj((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        status: !prev[key].status,
      },
    }));
  };

  const theme = useTheme();

  // Reset will set all statuses to true.
  const handleReset = () => {
    const resetObj = Object.fromEntries(
      Object.entries(columnObj).map(([key, value]) => [key, { ...value, status: true }]),
    ) as ColumnObjType;
    setTempColumnObj(resetObj);

    // Reset tile selections to show all tiles (no "none" selected)
    if (tilesObj) {
      const resetTileSelections = Object.keys(tilesObj).reduce((acc, key) => {
        acc[key] = tilesObj[key].name; // Reset to original tile names
        return acc;
      }, {} as Record<string, string>);
      setTileSelections(resetTileSelections);
    }

    if (onReset) {
      onReset();
    }
  };

  const handleSave = () => {
    setColumnObj(tempColumnObj);

    if (setTilesObj && tilesObj) {
      console.log('=== SAVE DEBUG ===');
      console.log('Current tileSelections:', tileSelections);

      // Create updated tiles object with correct status and name based on selections
      const updatedTileObj = Object.keys(tilesObj).reduce((acc, key) => {
        const selectedValue = tileSelections[key];
        const isNoneSelected = !selectedValue || selectedValue === 'none' || selectedValue === '';

        console.log(`${key}: selectedValue="${selectedValue}", isNone=${isNoneSelected}`);

        // Preserve the original name when deselecting (setting to "none")
        // This ensures the option remains available in other dropdowns
        const preservedName = isNoneSelected
          ? tilesObj[key].name || tilesObj[key].selected || '' // Keep original name if exists
          : selectedValue;

        acc[key] = {
          ...tilesObj[key],
          name: isNoneSelected ? '' : selectedValue, // Empty name when deselected
          status: !isNoneSelected,
          selected: preservedName, // Preserve original name for dropdown options
        };

        console.log(`  -> name="${acc[key].name}", status=${acc[key].status}, preserved="${preservedName}"`);
        return acc;
      }, {} as TilesObjType);

      console.log('Final updatedTileObj:', updatedTileObj);
      setTilesObj(updatedTileObj);
    }

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Build the complete list of dropdown options from tilesObj.
  // This should include ALL possible stat values, not just current selections
  const statsOption = useMemo(() => {
    if (tilesObj) {
      // Start with all possible stats captured on first mount
      const allStatNames = new Set<string>(allPossibleStats);

      // Add all current names from tilesObj
      Object.keys(tilesObj).forEach((key) => {
        if (tilesObj[key].name && tilesObj[key].name !== 'none' && tilesObj[key].name !== '') {
          allStatNames.add(tilesObj[key].name);
        }
        // Also check the selected property if it exists
        if (tilesObj[key].selected && tilesObj[key].selected !== 'none' && tilesObj[key].selected !== '') {
          allStatNames.add(tilesObj[key].selected);
        }
      });

      // Also add currently selected values from tileSelections (for newly selected options)
      Object.values(tileSelections).forEach((value) => {
        if (value && value !== 'none' && value !== '') {
          allStatNames.add(value);
        }
      });

      // Convert to options array
      const tiles = Array.from(allStatNames).map((name) => ({
        tileCode: name,
        tileName: name,
        label: name,
      }));

      // Add the "None" option
      tiles.push({
        tileCode: 'none',
        tileName: 'None',
        label: 'None',
      });

      return tiles;
    }
    return [];
  }, [tilesObj, tileSelections, allPossibleStats]);

  const handleTileChange = (tileKey: string, selectedValue: string) => {
    setTileSelections((prev) => {
      const next = { ...prev, [tileKey]: selectedValue };

      // Immediately reflect changes in the tiles object so other components
      // (like DriverCoachingDataTiles) update live while the modal is open.
      if (setTilesObj && tilesObj) {
        const updatedTileObj = Object.keys(tilesObj).reduce((acc, key) => {
          const sel = next[key];
          const isNoneSelected = !sel || sel === 'none';
          acc[key] = {
            ...tilesObj[key],
            name: isNoneSelected ? tilesObj[key].name : sel,
            status: !isNoneSelected,
            selected: sel || '',
          };
          return acc;
        }, {} as typeof tilesObj);

        try {
          setTilesObj(updatedTileObj as any);
        } catch (err) {
          // ignore if parent can't accept shape
          // console.error('setTilesObj failed', err);
        }
      }

      console.log(`Tile ${tileKey} changed to:`, selectedValue);
      return next;
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="customise-table-title"
      aria-describedby="customise-table-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '85%', md: '896px' },
          maxWidth: '896px',
          height: { xs: '90vh', sm: '85vh', md: '85vh' },
          maxHeight: '880px',
          display: 'flex',
          flexDirection: 'column',
          // justifyContent: 'space-between',
          borderRadius: '16px',
          border: '1px solid #F0EFF8',
          padding: { xs: '16px', sm: '24px', md: '32px' },
          background: '#FFFFFF',
          opacity: 1,
          boxShadow: 24,
        }}
      >
          <div className="flex flex-col gap-1 sticky mb-4">
            <div className="flex justify-between items-center">
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Inter Tight',
                  fontWeight: 600,
                  fontSize: '24px',
                  lineHeight: '36px',
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {title}
              </Typography>
              {onReset && (
                <Box
                  sx={{
                    // width: '88px',
                    height: '34px',
                    '& button': {
                      width: '100%',
                      height: '100%',
                      gap: '8px',
                      borderRadius: '8px',
                      border: '1px solid #5547DD',
                      padding: '8px 16px',
                      opacity: 1,
                    },
                  }}
                >
                  <CustomIconButton
                    text="Reset"
                    icon={<img src={refresh} alt="reset" />}
                    background="transparent"
                    variant="outlined"
                    onClick={handleReset}
                  />
                </Box>
              )}
            </div>
            {description && (
              <Typography
                variant="button"
                sx={{
                  fontFamily: 'Inter Tight',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0%',
                  color: '#767676',
                }}
              >
                {description}
              </Typography>
            )}
          </div>
        <div className="flex gap-8 flex-col overflow-y-auto scrollbar-hide">
          {tilesObj && (
            <div className="flex flex-col gap-2">
              <div
                className="flex justify-between w-full items-center cursor-pointer"
                onClick={toggleOverview}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'Inter Tight',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '27px',
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                  }}
                >
                  Top Overview Stats
                </Typography>
                <img
                  src={overviewExpanded ? arrowUp : arrowDown}
                  width={24}
                  height={24}
                  alt={overviewExpanded ? 'collapse' : 'expand'}
                />
              </div>
              {overviewExpanded && (
                <div className="flex gap-2">
                  {tilesObj &&
                    Object.keys(tilesObj).map((tileKey: string) => {
                      // Determine values selected in other dropdowns.
                      const alreadySelected = Object.keys(tileSelections)
                        .filter((key) => key !== tileKey)
                        .map((key) => tileSelections[key])
                        .filter((val) => val !== '' && val !== 'none');

                      // Filter statsOption and map to OptionType.
                      const filteredOptions: OptionType[] = statsOption
                        .filter((option) => !alreadySelected.includes(option.tileCode))
                        .map((option) => ({ value: option.tileCode, label: option.label }));

                      // Format label: "Stat1" -> "Stat 1", "Stat2" -> "Stat 2", etc.
                      const formattedLabel = tileKey.replace(/(\D+)(\d+)/, '$1 $2');

                      return (
                        <CustomDropDownInput
                          key={tileKey}
                          label={formattedLabel}
                          isMandatory={false}
                          options={filteredOptions}
                          value={tileSelections[tileKey] || ''}
                          onChange={(selectedValue: string) => handleTileChange(tileKey, selectedValue)}
                          placeholder="Select Route Code"
                          showSearch={false}
                          showDropdownArrow={true}
                          showCheckbox={true}
                        />
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Section: Data Table Columns */}
          <div className="flex flex-col gap-2">
            <div
              className="flex justify-between w-full items-center cursor-pointer"
              onClick={toggleDataTable}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Inter Tight',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '27px',
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                Data Table Columns
              </Typography>
              <img
                src={dataTableExpanded ? arrowUp : arrowDown}
                width={24}
                height={24}
                alt={dataTableExpanded ? 'collapse' : 'expand'}
              />
            </div>
            {dataTableExpanded && (
              <Box
                className="flex flex-col gap-2 custom-scrollbar"
                sx={{
                  maxHeight: '390px',
                  overflowY: 'auto',
                  borderRadius: '4px',
                  // padding: '8px',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {Object.entries(tempColumnObj)
                  .sort(([, a], [, b]) => {
                    // Sort: enabled items first (status: true), then disabled items (status: false)
                    if (a.status === b.status) return 0;
                    return a.status ? -1 : 1;
                  })
                  .map(([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between w-full items-center"
                      style={{
                        height: '56px',
                        gap: '16px',
                        borderRadius: '8px',
                        padding: '16px',
                        background: value.status ? '#F6F6FD' : '#E0E0E0',
                        transition: 'background 0.2s ease-in-out',
                      }}
                    >
                      <div className="flex gap-4 items-center">
                        <img
                          src={dragAndDrop}
                          width={24}
                          height={24}
                          alt="dragAndDrop"
                          style={{
                            opacity: value.status ? 1 : 0.5,
                            transition: 'opacity 0.2s ease-in-out',
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Inter Tight',
                            fontWeight: 600,
                            fontStyle: 'normal',
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0%',
                            color: '#1B1B1B',
                            opacity: value.status ? 1 : 0.5,
                            transition: 'opacity 0.2s ease-in-out',
                          }}
                        >
                          {value.name}
                        </Typography>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={value.status}
                          onChange={() => handleCheckboxChange(key as keyof ColumnObjType)}
                        />
                        <Box
                          className="flex items-center rounded-full transition-colors duration-300 ease-in-out"
                          sx={{
                            width: 36,
                            height: 20,
                            backgroundColor: value.status
                              ? theme.palette.primary.main
                              : theme.palette.secondary.main,
                            cursor: 'pointer',
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              transform: value.status ? 'translateX(18px)' : 'translateX(2px)',
                              transition: 'transform 300ms ease-in-out',
                            }}
                          />
                        </Box>
                      </label>
                    </div>
                  ))}
              </Box>
            )}
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex gap-2 mt-4 items-center justify-between sticky">
          <CustomIconButton
            text="Cancel"
            background="transparent"
            outline={true}
            padding="0px"
            variant="outlined"
            onClick={handleCancel}
          />
          <CustomIconButton
            text="Save"
            outline={true}
            padding="0px"
            variant="contained"
            onClick={handleSave}
          />
        </div>
      </Box>
    </Modal>
  );
};

export default CustomiseTableModal;
